import { FormEvent, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import type { VocabItem } from "@/lib/types";
import { useProfileStore } from "@/state/profile";
import { useSessionStore } from "@/state/session";
import { GameShell } from "@/components/GameShell";
import { FeedbackOverlay } from "@/components/FeedbackOverlay";
import { burstConfetti, sfxCorrect, sfxWrong } from "@/lib/rewards";
import { gradeLocally } from "@/lib/grade";
import { SpeakButton } from "@/components/SpeakButton";

export function VocabGame() {
  return (
    <>
      <GameShell game="vocab">
        {({ item, disabled }) => {
          if (item.kind !== "vocab") return null;
          return <VocabCard item={item} disabled={disabled} />;
        }}
      </GameShell>
      <VocabFeedback />
    </>
  );
}

function VocabCard({ item, disabled }: { item: VocabItem; disabled: boolean }) {
  const profileId = useProfileStore((s) => s.activeId);
  const answer = useSessionStore((s) => s.answer);
  const support = useSessionStore((s) => s.loaded?.session.support_level ?? "standard");
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue("");
    setHintOpen(support === "guided");
    // Focus the input whenever a new item lands.
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [item.id, support]);

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (disabled || submitting || !value.trim()) return;
    await sendAnswer(value.trim());
  }

  async function sendAnswer(payload: string) {
    if (disabled || submitting) return;
    setSubmitting(true);
    // Optimistic feedback based on local exact-match. Server may upgrade a
    // near-miss to correct via AI adjudication — the overlay will flip when
    // that happens.
    const { correct } = gradeLocally(item, payload);
    if (correct) {
      sfxCorrect((useSessionStore.getState().loaded?.combo ?? 0) + 1);
      burstConfetti("#26C3A5");
    } else {
      sfxWrong();
    }
    await answer(profileId, payload);
    setSubmitting(false);
  }

  const targetLang = item.direction === "en2es" ? "español" : "inglés";

  return (
    <motion.form
      key={item.id}
      onSubmit={submit}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="h-full flex flex-col items-center justify-start pt-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="pill">{item.category} · {levelLabel(item.difficulty)}</span>
        <span className="pill bg-brand-violet/10 border-brand-violet/30 text-brand-violet">
          {item.direction === "en2es" ? "EN → ES" : "ES → EN"}
        </span>
      </div>

      <motion.div
        key={item.prompt}
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="chunk px-6 py-10 w-full max-w-md text-center"
      >
        <div className="text-xs text-brand-muted uppercase tracking-widest font-black">
          ¿Cómo se dice en {targetLang}?
        </div>
        <div className="mt-2 font-display text-3xl sm:text-4xl leading-tight break-words">
          {item.prompt}
        </div>
        {item.direction === "es2en" && (
          <div className="mt-3 flex justify-center"><SpeakButton text={item.prompt} /></div>
        )}
        {item.dialect && item.dialect !== "both" && (
          <div className="mt-3 text-xs font-bold text-brand-muted">
            dialecto: {item.dialect === "es" ? "España" : item.dialect === "ar" ? "Argentina" : item.dialect}
          </div>
        )}
      </motion.div>

      <div className="w-full max-w-md mt-6">
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="send"
          placeholder="Escribe tu respuesta…"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={disabled || submitting}
          className="w-full text-center text-2xl font-display px-5 py-5 rounded-chunk bg-brand-surface border-2 border-brand-border focus:border-brand-coral focus:outline-none shadow-chunk"
        />
        <button
          type="submit"
          disabled={disabled || submitting || !value.trim()}
          className="chunk-btn-primary w-full mt-3 disabled:opacity-50"
        >
          {submitting ? "Enviando…" : "Comprobar"}
        </button>
        {support !== "challenge" && (
          <button
            type="button"
            disabled={disabled || submitting}
            onClick={() => setHintOpen((open) => !open)}
            className="chunk-btn-ghost w-full mt-1 text-brand-violet"
          >
            {hintOpen ? "Ocultar pista" : "Ver una pista"}
          </button>
        )}
        {hintOpen && (
          <div className="rounded-2xl bg-brand-violet/10 border border-brand-violet/25 px-4 py-3 text-center text-sm font-extrabold text-brand-ink">
            Empieza así: <span className="font-display">{maskHint(item.answers[0] ?? "")}</span>
          </div>
        )}
        <button
          type="button"
          disabled={disabled || submitting}
          onClick={() => sendAnswer("")}
          className="chunk-btn-ghost w-full mt-1 text-brand-muted underline decoration-dotted underline-offset-4"
        >
          No sé
        </button>
      </div>
    </motion.form>
  );
}

function VocabFeedback() {
  const loaded = useSessionStore((s) => s.loaded);
  const advance = useSessionStore((s) => s.advance);
  return <FeedbackOverlay result={loaded?.lastResult ?? null} onNext={advance} />;
}

function levelLabel(d: 1 | 2 | 3): string {
  return d === 1 ? "Esencial" : d === 2 ? "Conversación" : "Reto";
}

function maskHint(answer: string): string {
  return answer
    .split(/(\s+)/)
    .map((part) => {
      if (/^\s+$/.test(part)) return part;
      const letters = Array.from(part);
      return letters
        .map((letter, index) =>
          index === 0 || /[^\p{L}]/u.test(letter) ? letter : "_",
        )
        .join("");
    })
    .join("");
}
